<template>
  <div class="Hero">
    <div class="relative w-full min-h-full select-none">
      <video
        poster="https://d3s6jo6q0vpov5.cloudfront.net/homepage-video-poster-2.jpg"
        loop="loop"
        autoplay="autoplay"
        playsinline=""
        muted="muted"
        disablepictureinpicture="disablepictureinpicture"
        fetchpriority="high"
        class="Hero__video"
      >
        <source src="https://d3s6jo6q0vpov5.cloudfront.net/homepage-video-2.mp4" type="video/mp4" />
        <source src="https://d3s6jo6q0vpov5.cloudfront.net/homepage-video-2.webm" type="video/webm" />
      </video>

      <div class="Hero__overlay">
        <HomepageHeroInsight />
        <div class="max-w-page lg:pt-36 lg:pb-12 relative z-10 flex flex-col flex-1 w-full px-8 pt-48 pb-8 mx-auto">
          <div class="Hero__badge self-start inline-block px-4 py-2 mb-6 text-sm text-gray-400 transition-all cursor-pointer">
            <router-link :to="{ name: 'cyclingTrainingPlans' }" title="Adaptive cycling training plans" class="font-medium">
              <span style="color: #b6ffc9" class="font-bold">New</span>
              <span class="px-0.5">&bull;</span>
              Adaptive training plans that fit your week
            </router-link>
          </div>
          <div class="Hero__heading">Faster,</div>
          <div class="Hero__heading">Further</div>
          <!-- prettier-ignore -->
          <h1 class="md:w-1/2 xl:w-2/5 md:text-xl xl:text-2xl opacity-90 my-6 mb-8 text-xl font-normal">
            Your rides hold secrets to your cycling potential. Our AI-powered analytics uncover the patterns that help you become a stronger cyclist.
          </h1>
          <div class="sm:flex-row flex flex-col items-center">
            <router-link v-if="!user" :to="{ name: 'join' }" class="sm:w-auto w-full mb-6" title="Sign up">
              <AppButton primary block class="w-full">Get started</AppButton>
            </router-link>

            <router-link v-else :to="{ name: 'dashboard' }" class="sm:w-auto w-full mb-6" title="Training Dashboard">
              <AppButton primary block class="w-full">Go to Dashboard</AppButton>
            </router-link>

            <div class="md:hidden flex flex-col items-center justify-center gap-4">
              <a :href="appStoreInfo.url" target="_blank">
                <img :src="appStoreInfo.icon" :alt="appStoreInfo.alt" class="h-14 md:h-16" />
              </a>
            </div>

            <img src="~images/strava-badge-white.svg" alt="" class="md:absolute top-0 right-0 hidden w-24 mt-8 mr-8" />
          </div>

          <div class="md:mt-auto md:pt-8 left-0 right-0 mt-16">
            <div class="md:flex-row md:space-y-0 flex flex-col items-center justify-between space-y-12">
              <div class="md:w-auto w-full">
                <div class="md:text-left text-xs font-bold uppercase">Works with</div>
                <div class="opacity-80 flex flex-wrap items-center gap-6 mt-3">
                  <img src="~images/logos/strava.svg" alt="Strava" class="md:w-28 w-20" />
                  <img src="~images/logos/garmin.svg" alt="Garmin" class="md:w-28 w-20" />
                  <img src="~images/logos/zwift.svg" alt="Zwift" class="md:block md:w-24 hidden w-20" />
                  <div class="text-base">&amp; more...</div>
                </div>
              </div>

              <div class="md:hidden lg:block md:w-auto block w-full">
                <div class="md:text-left text-xs font-bold uppercase">Featured in</div>
                <div class="opacity-80 flex flex-wrap items-center gap-6 mt-3">
                  <img src="~images/logos/roadcc.svg" alt="Road.cc" class="md:w-40 w-36" />
                  <img src="~images/logos/cobbles.png" alt="Cobbles cycling" class="md:w-20 w-14" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getAppStoreInfo } from 'utils/device'
import HomepageHeroInsight from 'components/HomepageHeroInsight'

export default {
  components: {
    HomepageHeroInsight
  },

  computed: {
    ...mapGetters('profile', ['user']),
    appStoreInfo() {
      return getAppStoreInfo()
    }
  }
}
</script>

<style lang="postcss" scoped>
.Hero {
  @apply relative w-full;
  /* Dark base behind the video + overlay. In Safari the overlay's translucent tint isn't
     composited over the autoplaying video until its first frame settles; without this base
     the bright video flashes through and the white headings are briefly unreadable. */
  background-color: #0e0c09;
}
/* The overlay holds the hero content in normal flow, so its height (and therefore the
   hero's) grows with the content. min-height keeps it full-viewport when content is short;
   the flex column lets the bottom logo strip pin to the base via mt-auto without overlapping
   the CTA on short viewports. */
.Hero__overlay {
  @apply relative flex flex-col text-white;
  min-height: min(100vh, 1024px);
  width: 100%;
  z-index: 3;
  background: rgba(14, 12, 9, 0.65);
  overflow: hidden;
  /* Promote the overlay to its own compositing layer so Safari paints the translucent tint
     from the first frame, instead of after the video layer settles (the tint-appears-late flash). */
  transform: translateZ(0);
  @media only screen and (min-width: theme('screens.md')) {
    background: rgba(14, 12, 9, 0.6);
  }
}
/* Fade the bottom of the video into the next section's colour (deepblue #001116) so the
   seam between the hero and the section below disappears. Sits above the video but below the
   hero content (which is lifted to z-10), so only the background darkens. */
.Hero__overlay::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40%;
  z-index: 2;
  pointer-events: none;
  background: linear-gradient(to bottom, rgba(0, 17, 22, 0) 0%, rgba(0, 17, 22, 0.85) 65%, #001116 100%);
}
.Hero__video {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
  object-fit: cover;
}
/* Frosted pill for the "New:" badge. backdrop-filter is applied here (not via a Tailwind
   class) because this Tailwind 2.0 build doesn't generate the backdrop-blur-* utilities. */
.Hero__badge {
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 9999px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.Hero__badge:hover {
  background: rgba(0, 0, 0, 0.5);
}

.Hero__heading {
  @apply font-bold;
  font-size: 4rem;
  line-height: 1;

  @media only screen and (min-width: theme('screens.md')) {
    font-size: 6rem;
    line-height: 0.95;
  }

  @media only screen and (min-width: theme('screens.lg')) {
    font-size: 8rem;
    line-height: 0.95;
  }

  @media only screen and (min-width: theme('screens.2xl')) {
    font-size: 9rem;
    line-height: 0.95;
  }
}
</style>
